import React, { lazy, Suspense } from 'react';
import { useAuth } from '../components/AuthProvider';
import { useLocaleContext } from '../context/LocaleContext';
import WatermarkRemoverClient from '../components/WatermarkRemover.client';
import { SocialProof } from '../components/SocialProof';
import { generateFAQSchema, injectFAQSchema } from '../utils/faqSchema';
import { RelatedContentForPSEO } from '../components/RelatedContentForPSEO';

// Lazy load below-the-fold components for better performance
const FeedbackCTA = lazy(() => import('../components/FeedbackCTA'));
const RelatedTools = lazy(() => import('../components/RelatedTools'));
import GeminiImageBanner from '../components/GeminiImageBanner';
import {
  CheckCircle2,
  Shield,
  Zap,
  FileText,
  GraduationCap,
  Briefcase,
  PenTool,
  ChevronDown,
  ArrowRight,
  Sparkles,
  HelpCircle,
  Gift,
  Lock,
  Infinity,
  BookOpen,
} from 'lucide-react';

type LocaleKey = 'en' | 'de' | 'pt-BR' | 'ms-MY' | 'es' | 'fr' | 'ja' | 'id';

export default function FreeChatGPTWatermarkRemoverPage() {
  const { user } = useAuth();
  const { locale } = useLocaleContext();
  const [openFAQIndex, setOpenFAQIndex] = React.useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenFAQIndex(openFAQIndex === index ? null : index);
  };

  // Translations object
  const t = {
    hero: {
      badge: {
        en: '100% Free - No Signup Required',
        de: '100% Kostenlos - Keine Anmeldung erforderlich',
        'pt-BR': '100% Gratis - Sem cadastro necessario',
        'ms-MY': '100% Percuma - Tiada Pendaftaran Diperlukan',
        es: '100% Gratis - Sin registro necesario',
        fr: '100% Gratuit - Aucune inscription requise',
        ja: '100%無料 - 登録不要',
        id: '100% Gratis - Tanpa Pendaftaran',
      },
      title: {
        en: 'Free ChatGPT Watermark Remover',
        de: 'Kostenloser ChatGPT Wasserzeichen-Entferner',
        'pt-BR': 'Removedor de Marca d\'agua ChatGPT Gratis',
        'ms-MY': 'Penyingkir Tanda Air ChatGPT Percuma',
        es: 'Eliminador de Marca de Agua ChatGPT Gratis',
        fr: 'Supprimer le Filigrane ChatGPT Gratuitement',
        ja: '無料ChatGPT透かし削除ツール',
        id: 'Hapus Watermark ChatGPT Gratis',
      },
      subtitle: {
        en: 'Remove invisible watermarks from ChatGPT text instantly. No signup, no payment, no limits on uses. 100% browser-based and private.',
        de: 'Entfernen Sie unsichtbare Wasserzeichen aus ChatGPT-Text sofort. Keine Anmeldung, keine Zahlung, keine Nutzungsbeschraenkungen. 100% browserbasiert und privat.',
        'pt-BR': 'Remova marcas d\'agua invisiveis do texto ChatGPT instantaneamente. Sem cadastro, sem pagamento, sem limites de uso. 100% baseado no navegador e privado.',
        'ms-MY': 'Buang tanda air tidak kelihatan dari teks ChatGPT serta-merta. Tiada pendaftaran, tiada bayaran, tiada had penggunaan. 100% berasaskan pelayar dan peribadi.',
        es: 'Elimina marcas de agua invisibles del texto de ChatGPT al instante. Sin registro, sin pago, sin limites de uso. 100% basado en navegador y privado.',
        fr: 'Supprimez les filigranes invisibles du texte ChatGPT instantanement. Sans inscription, sans paiement, sans limite d\'utilisation. 100% dans votre navigateur et prive.',
        ja: 'ChatGPTテキストから不可視の透かしを瞬時に削除。登録不要、支払い不要、使用回数無制限。100%ブラウザベースでプライベート。',
        id: 'Hapus watermark tak terlihat dari teks ChatGPT secara instan. Tanpa pendaftaran, tanpa pembayaran, tanpa batasan penggunaan. 100% berbasis browser dan privat.',
      },
      cta: {
        en: 'Remove Watermarks Free',
        de: 'Wasserzeichen kostenlos entfernen',
        'pt-BR': 'Remover Marcas d\'agua Gratis',
        'ms-MY': 'Buang Tanda Air Percuma',
        es: 'Eliminar Marcas de Agua Gratis',
        fr: 'Supprimer les Filigranes Gratuitement',
        ja: '無料で透かしを削除',
        id: 'Hapus Watermark Gratis',
      },
    },
    toolSection: {
      title: {
        en: 'Free ChatGPT Watermark Removal Tool',
        de: 'Kostenloses ChatGPT Wasserzeichen-Entfernungs-Tool',
        'pt-BR': 'Ferramenta Gratuita de Remocao de Marca d\'agua ChatGPT',
        'ms-MY': 'Alat Penyingkiran Tanda Air ChatGPT Percuma',
        es: 'Herramienta Gratuita para Eliminar Marcas de Agua de ChatGPT',
        fr: 'Outil Gratuit de Suppression de Filigrane ChatGPT',
        ja: '無料ChatGPT透かし削除ツール',
        id: 'Alat Hapus Watermark ChatGPT Gratis',
      },
      description: {
        en: 'Paste your ChatGPT-generated text below. Our free tool will find and strip the invisible Unicode characters listed below.',
        de: 'Fuegen Sie Ihren ChatGPT-generierten Text unten ein. Unser kostenloses Tool findet und entfernt die unten aufgefuehrten unsichtbaren Unicode-Zeichen.',
        'pt-BR': 'Cole seu texto gerado pelo ChatGPT abaixo. Nossa ferramenta gratuita encontrara e removera os caracteres Unicode invisiveis listados abaixo.',
        'ms-MY': 'Tampal teks yang dijana ChatGPT anda di bawah. Alat percuma kami akan mencari dan membuang aksara Unicode tidak kelihatan yang disenaraikan di bawah.',
        es: 'Pega tu texto generado por ChatGPT abajo. Nuestra herramienta gratuita encontrara y eliminara los caracteres Unicode invisibles enumerados abajo.',
        fr: 'Collez votre texte genere par ChatGPT ci-dessous. Notre outil gratuit reperera et supprimera les caracteres Unicode invisibles enumeres ci-dessous.',
        ja: 'ChatGPTで生成したテキストを下に貼り付けてください。無料ツールが以下に記載された不可視のUnicode文字を見つけて除去します。',
        id: 'Tempelkan teks yang dihasilkan ChatGPT di bawah ini. Alat gratis kami akan menemukan dan menghapus karakter Unicode tak terlihat yang tercantum di bawah.',
      },
    },
    benefits: {
      title: {
        en: 'What You Get for Free',
        de: 'Was Sie kostenlos bekommen',
        'pt-BR': 'O Que Voce Recebe Gratis',
        'ms-MY': 'Apa Yang Anda Dapat Secara Percuma',
        es: 'Lo Que Obtienes Gratis',
        fr: 'Ce Que Vous Obtenez Gratuitement',
        ja: '無料で得られるもの',
        id: 'Yang Anda Dapatkan Gratis',
      },
      items: {
        en: [
          { icon: Gift, title: '500 Characters Free', description: 'Clean up to 500 characters per paste without any account' },
          { icon: Lock, title: 'No Signup Required', description: 'Start using immediately - no email, no password, no hassle' },
          { icon: Infinity, title: 'Unlimited Uses', description: 'Use the free tier as many times as you need, forever' },
          { icon: Shield, title: '100% Private', description: 'All processing happens in your browser - we never see your text' },
        ],
        de: [
          { icon: Gift, title: '500 Zeichen kostenlos', description: 'Bereinigen Sie bis zu 500 Zeichen pro Einfuegen ohne Konto' },
          { icon: Lock, title: 'Keine Anmeldung erforderlich', description: 'Sofort loslegen - keine E-Mail, kein Passwort, kein Aufwand' },
          { icon: Infinity, title: 'Unbegrenzte Nutzung', description: 'Nutzen Sie die kostenlose Version so oft Sie moechten, fuer immer' },
          { icon: Shield, title: '100% Privat', description: 'Alle Verarbeitung erfolgt in Ihrem Browser - wir sehen Ihren Text nie' },
        ],
        'pt-BR': [
          { icon: Gift, title: '500 Caracteres Gratis', description: 'Limpe ate 500 caracteres por colagem sem nenhuma conta' },
          { icon: Lock, title: 'Sem Cadastro Necessario', description: 'Comece a usar imediatamente - sem email, sem senha, sem complicacao' },
          { icon: Infinity, title: 'Usos Ilimitados', description: 'Use a versao gratuita quantas vezes precisar, para sempre' },
          { icon: Shield, title: '100% Privado', description: 'Todo o processamento acontece no seu navegador - nunca vemos seu texto' },
        ],
        'ms-MY': [
          { icon: Gift, title: '500 Aksara Percuma', description: 'Bersihkan sehingga 500 aksara setiap tampal tanpa akaun' },
          { icon: Lock, title: 'Tiada Pendaftaran Diperlukan', description: 'Mula gunakan segera - tiada emel, tiada kata laluan, tiada kerumitan' },
          { icon: Infinity, title: 'Penggunaan Tanpa Had', description: 'Gunakan peringkat percuma seberapa banyak yang anda perlukan, selamanya' },
          { icon: Shield, title: '100% Peribadi', description: 'Semua pemprosesan berlaku dalam pelayar anda - kami tidak melihat teks anda' },
        ],
        es: [
          { icon: Gift, title: '500 Caracteres Gratis', description: 'Limpia hasta 500 caracteres por pegado sin ninguna cuenta' },
          { icon: Lock, title: 'Sin Registro Necesario', description: 'Comienza a usar inmediatamente - sin email, sin contrasena, sin complicaciones' },
          { icon: Infinity, title: 'Usos Ilimitados', description: 'Usa la version gratuita tantas veces como necesites, para siempre' },
          { icon: Shield, title: '100% Privado', description: 'Todo el procesamiento ocurre en tu navegador - nunca vemos tu texto' },
        ],
        fr: [
          { icon: Gift, title: '500 Caracteres Gratuits', description: 'Nettoyez jusqu\'a 500 caracteres par collage sans compte' },
          { icon: Lock, title: 'Aucune Inscription Requise', description: 'Utilisez immediatement - sans email, sans mot de passe, sans tracas' },
          { icon: Infinity, title: 'Utilisations Illimitees', description: 'Utilisez la version gratuite autant de fois que necessaire, pour toujours' },
          { icon: Shield, title: '100% Prive', description: 'Tout le traitement se fait dans votre navigateur - nous ne voyons jamais votre texte' },
        ],
        ja: [
          { icon: Gift, title: '500文字無料', description: 'アカウントなしで貼り付けごとに最大500文字をクリーニング' },
          { icon: Lock, title: '登録不要', description: 'すぐに使い始められます - メール不要、パスワード不要、手間なし' },
          { icon: Infinity, title: '無制限に使用可能', description: '無料版を必要なだけ何度でも、永久に使用できます' },
          { icon: Shield, title: '100%プライベート', description: 'すべての処理はブラウザ内で行われ、テキストを私たちが見ることはありません' },
        ],
        id: [
          { icon: Gift, title: '500 Karakter Gratis', description: 'Bersihkan hingga 500 karakter per tempel tanpa akun' },
          { icon: Lock, title: 'Tanpa Pendaftaran', description: 'Langsung gunakan - tanpa email, tanpa kata sandi, tanpa ribet' },
          { icon: Infinity, title: 'Penggunaan Tanpa Batas', description: 'Gunakan tingkat gratis sebanyak yang Anda butuhkan, selamanya' },
          { icon: Shield, title: '100% Privat', description: 'Semua pemrosesan terjadi di browser Anda - kami tidak pernah melihat teks Anda' },
        ],
      },
    },
    explanation: {
      title: {
        en: 'How the Free ChatGPT Watermark Remover Works',
        de: 'Wie der kostenlose ChatGPT Wasserzeichen-Entferner funktioniert',
        'pt-BR': 'Como o Removedor de Marca d\'agua ChatGPT Gratis Funciona',
        'ms-MY': 'Bagaimana Penyingkir Tanda Air ChatGPT Percuma Berfungsi',
        es: 'Como Funciona el Eliminador de Marcas de Agua ChatGPT Gratis',
        fr: 'Comment Fonctionne l\'Outil Gratuit de Suppression de Filigrane ChatGPT',
        ja: '無料ChatGPT透かし削除ツールの仕組み',
        id: 'Cara Kerja Alat Hapus Watermark ChatGPT Gratis',
      },
      paragraphs: {
        en: [
          'Text copied out of ChatGPT and other AI models sometimes carries invisible Unicode characters. They\'re completely invisible to the human eye but can be detected by specialized software.',
          'Our free tool scans your text for these hidden characters - including zero-width spaces (U+200B), zero-width joiners (U+200D), and word joiners (U+2060) - and removes them instantly.',
          'The process is 100% client-side, meaning your text never leaves your browser. This ensures complete privacy while giving you clean text.',
        ],
        de: [
          'Text, der aus ChatGPT und anderen KI-Modellen kopiert wird, enthaelt manchmal unsichtbare Unicode-Zeichen. Sie sind fuer das menschliche Auge voellig unsichtbar, koennen aber von spezialisierter Software erkannt werden.',
          'Unser kostenloses Tool scannt Ihren Text nach diesen versteckten Zeichen - einschliesslich Zero-Width-Spaces (U+200B), Zero-Width-Joinern (U+200D) und Word-Joinern (U+2060) - und entfernt sie sofort.',
          'Der Prozess ist zu 100% clientseitig, was bedeutet, dass Ihr Text niemals Ihren Browser verlaesst. Dies gewaehrleistet vollstaendige Privatsphaere und gibt Ihnen sauberen Text.',
        ],
        'pt-BR': [
          'O texto copiado do ChatGPT e de outros modelos de IA as vezes carrega caracteres Unicode invisiveis. Eles sao completamente invisiveis ao olho humano, mas podem ser detectados por software especializado.',
          'Nossa ferramenta gratuita escaneia seu texto em busca desses caracteres ocultos - incluindo espacos de largura zero (U+200B), juncoes de largura zero (U+200D) e juncoes de palavras (U+2060) - e os remove instantaneamente.',
          'O processo e 100% do lado do cliente, o que significa que seu texto nunca sai do seu navegador. Isso garante privacidade completa enquanto oferece texto limpo.',
        ],
        'ms-MY': [
          'Teks yang disalin dari ChatGPT dan model AI lain kadangkala membawa aksara Unicode tidak kelihatan. Ia sama sekali tidak kelihatan kepada mata manusia tetapi boleh dikesan oleh perisian khusus.',
          'Alat percuma kami mengimbas teks anda untuk aksara tersembunyi ini - termasuk ruang lebar sifar (U+200B), penyambung lebar sifar (U+200D), dan penyambung perkataan (U+2060) - dan membuangnya serta-merta.',
          'Proses ini adalah 100% di sisi pelanggan, bermakna teks anda tidak pernah meninggalkan pelayar anda. Ini memastikan privasi lengkap sambil memberi anda teks yang bersih.',
        ],
        es: [
          'El texto copiado de ChatGPT y otros modelos de IA a veces contiene caracteres Unicode invisibles. Son completamente invisibles al ojo humano, pero pueden ser detectados por software especializado.',
          'Nuestra herramienta gratuita escanea tu texto en busca de estos caracteres ocultos - incluyendo espacios de ancho cero (U+200B), uniones de ancho cero (U+200D) y uniones de palabras (U+2060) - y los elimina instantaneamente.',
          'El proceso es 100% del lado del cliente, lo que significa que tu texto nunca sale de tu navegador. Esto garantiza privacidad completa mientras te da texto limpio.',
        ],
        fr: [
          'Le texte copie depuis ChatGPT et d\'autres modeles d\'IA contient parfois des caracteres Unicode invisibles. Ils sont completement invisibles a l\'oeil humain, mais detectables par des logiciels specialises.',
          'Notre outil gratuit analyse votre texte pour detecter ces caracteres caches - y compris les espaces de largeur nulle (U+200B), les jointeurs de largeur nulle (U+200D) et les jointeurs de mots (U+2060) - et les supprime instantanement.',
          'Le processus est 100% cote client, ce qui signifie que votre texte ne quitte jamais votre navigateur. Cela garantit une confidentialite totale tout en vous offrant un texte propre.',
        ],
        ja: [
          'ChatGPTや他のAIモデルからコピーしたテキストには、不可視のUnicode文字が含まれていることがあります。これらは人間の目には完全に見えませんが、専門のソフトウェアで検出できます。',
          '私たちの無料ツールは、ゼロ幅スペース（U+200B）、ゼロ幅結合子（U+200D）、ワードジョイナー（U+2060）などの隠し文字をテキストからスキャンし、瞬時に削除します。',
          'このプロセスは100%クライアントサイドで行われるため、テキストがブラウザを離れることはありません。完全なプライバシーを確保しながら、クリーンなテキストを提供します。',
        ],
        id: [
          'Teks yang disalin dari ChatGPT dan model AI lainnya kadang membawa karakter Unicode tak terlihat. Karakter ini sepenuhnya tak terlihat oleh mata manusia tetapi dapat dideteksi oleh perangkat lunak khusus.',
          'Alat gratis kami memindai teks Anda untuk karakter tersembunyi ini - termasuk spasi lebar-nol (U+200B), penyambung lebar-nol (U+200D), dan penyambung kata (U+2060) - dan menghapusnya secara instan.',
          'Prosesnya 100% di sisi klien, artinya teks Anda tidak pernah meninggalkan browser Anda. Ini memastikan privasi lengkap sambil memberikan teks yang bersih.',
        ],
      },
    },
    comparison: {
      title: {
        en: 'Free vs. Premium',
        de: 'Kostenlos vs. Premium',
        'pt-BR': 'Gratis vs. Premium',
        'ms-MY': 'Percuma vs. Premium',
        es: 'Gratis vs. Premium',
        fr: 'Gratuit vs. Premium',
        ja: '無料版 vs. プレミアム',
        id: 'Gratis vs. Premium',
      },
      featureLabel: {
        en: 'Feature',
        de: 'Funktion',
        'pt-BR': 'Recurso',
        'ms-MY': 'Ciri',
        es: 'Funcion',
        fr: 'Fonctionnalite',
        ja: '機能',
        id: 'Fitur',
      },
      free: {
        en: 'Free Tier',
        de: 'Kostenlose Version',
        'pt-BR': 'Versao Gratuita',
        'ms-MY': 'Peringkat Percuma',
        es: 'Version Gratuita',
        fr: 'Version Gratuite',
        ja: '無料版',
        id: 'Versi Gratis',
      },
      premium: {
        en: 'Premium',
        de: 'Premium',
        'pt-BR': 'Premium',
        'ms-MY': 'Premium',
        es: 'Premium',
        fr: 'Premium',
        ja: 'プレミアム',
        id: 'Premium',
      },
      features: {
        en: [
          { feature: 'Character Limit', free: '500 per paste', premium: 'Unlimited' },
          { feature: 'Signup Required', free: 'No', premium: 'Yes' },
          { feature: 'Daily Usage', free: 'Unlimited', premium: 'Unlimited' },
          { feature: 'Document Cleaning', free: 'Not included', premium: 'Word & Pages files' },
          { feature: 'Browser Extension', free: 'Basic features', premium: 'All features' },
        ],
        de: [
          { feature: 'Zeichenlimit', free: '500 pro Einfuegen', premium: 'Unbegrenzt' },
          { feature: 'Anmeldung erforderlich', free: 'Nein', premium: 'Ja' },
          { feature: 'Taegliche Nutzung', free: 'Unbegrenzt', premium: 'Unbegrenzt' },
          { feature: 'Dokumentenbereinigung', free: 'Nicht enthalten', premium: 'Word & Pages Dateien' },
          { feature: 'Browser-Erweiterung', free: 'Grundfunktionen', premium: 'Alle Funktionen' },
        ],
        'pt-BR': [
          { feature: 'Limite de Caracteres', free: '500 por colagem', premium: 'Ilimitado' },
          { feature: 'Cadastro Necessario', free: 'Nao', premium: 'Sim' },
          { feature: 'Uso Diario', free: 'Ilimitado', premium: 'Ilimitado' },
          { feature: 'Limpeza de Documentos', free: 'Nao incluido', premium: 'Arquivos Word e Pages' },
          { feature: 'Extensao do Navegador', free: 'Recursos basicos', premium: 'Todos os recursos' },
        ],
        'ms-MY': [
          { feature: 'Had Aksara', free: '500 setiap tampal', premium: 'Tanpa had' },
          { feature: 'Pendaftaran Diperlukan', free: 'Tidak', premium: 'Ya' },
          { feature: 'Penggunaan Harian', free: 'Tanpa had', premium: 'Tanpa had' },
          { feature: 'Pembersihan Dokumen', free: 'Tidak termasuk', premium: 'Fail Word & Pages' },
          { feature: 'Sambungan Pelayar', free: 'Ciri asas', premium: 'Semua ciri' },
        ],
        es: [
          { feature: 'Limite de Caracteres', free: '500 por pegado', premium: 'Ilimitado' },
          { feature: 'Registro Necesario', free: 'No', premium: 'Si' },
          { feature: 'Uso Diario', free: 'Ilimitado', premium: 'Ilimitado' },
          { feature: 'Limpieza de Documentos', free: 'No incluido', premium: 'Archivos Word y Pages' },
          { feature: 'Extension del Navegador', free: 'Funciones basicas', premium: 'Todas las funciones' },
        ],
        fr: [
          { feature: 'Limite de Caracteres', free: '500 par collage', premium: 'Illimite' },
          { feature: 'Inscription Requise', free: 'Non', premium: 'Oui' },
          { feature: 'Utilisation Quotidienne', free: 'Illimitee', premium: 'Illimitee' },
          { feature: 'Nettoyage de Documents', free: 'Non inclus', premium: 'Fichiers Word & Pages' },
          { feature: 'Extension de Navigateur', free: 'Fonctions de base', premium: 'Toutes les fonctions' },
        ],
        ja: [
          { feature: '文字数制限', free: '貼り付けごとに500文字', premium: '無制限' },
          { feature: '登録が必要', free: 'いいえ', premium: 'はい' },
          { feature: '毎日の使用', free: '無制限', premium: '無制限' },
          { feature: 'ドキュメントクリーニング', free: '含まれていません', premium: 'Word & Pagesファイル' },
          { feature: 'ブラウザ拡張機能', free: '基本機能', premium: 'すべての機能' },
        ],
        id: [
          { feature: 'Batas Karakter', free: '500 per tempel', premium: 'Tidak terbatas' },
          { feature: 'Pendaftaran Diperlukan', free: 'Tidak', premium: 'Ya' },
          { feature: 'Penggunaan Harian', free: 'Tidak terbatas', premium: 'Tidak terbatas' },
          { feature: 'Pembersihan Dokumen', free: 'Tidak termasuk', premium: 'File Word & Pages' },
          { feature: 'Ekstensi Browser', free: 'Fitur dasar', premium: 'Semua fitur' },
        ],
      },
    },
    useCases: {
      title: {
        en: 'Who Uses Our Free ChatGPT Watermark Remover?',
        de: 'Wer nutzt unseren kostenlosen ChatGPT Wasserzeichen-Entferner?',
        'pt-BR': 'Quem Usa Nosso Removedor de Marca d\'agua ChatGPT Gratis?',
        'ms-MY': 'Siapa yang Menggunakan Penyingkir Tanda Air ChatGPT Percuma Kami?',
        es: 'Quien Usa Nuestro Eliminador de Marcas de Agua ChatGPT Gratis?',
        fr: 'Qui Utilise Notre Outil Gratuit de Suppression de Filigrane ChatGPT ?',
        ja: '無料ChatGPT透かし削除ツールを使うのは誰？',
        id: 'Siapa yang Menggunakan Alat Hapus Watermark ChatGPT Gratis Kami?',
      },
      items: {
        en: [
          { icon: GraduationCap, title: 'Students', description: 'Clean AI-assisted research notes and drafts so hidden characters don\'t corrupt citations, break formatting, or skew word and character counts' },
          { icon: PenTool, title: 'Writers & Bloggers', description: 'Remove watermarks from AI-assisted content for publishing' },
          { icon: FileText, title: 'Word/Google Docs Users', description: 'Clean text before pasting into documents to avoid formatting issues' },
          { icon: Briefcase, title: 'Professionals', description: 'Ensure business communications are clean and professional' },
        ],
        de: [
          { icon: GraduationCap, title: 'Studierende', description: 'KI-unterstuetzte Recherche-Notizen und Entwuerfe bereinigen, damit versteckte Zeichen keine Zitate beschaedigen, keine Formatierung stoeren und keine Wort- und Zeichenzaehlungen verfaelschen' },
          { icon: PenTool, title: 'Autoren & Blogger', description: 'Wasserzeichen aus KI-unterstuetzten Inhalten zur Veroeffentlichung entfernen' },
          { icon: FileText, title: 'Word/Google Docs Nutzer', description: 'Text vor dem Einfuegen in Dokumente bereinigen, um Formatierungsprobleme zu vermeiden' },
          { icon: Briefcase, title: 'Berufstaetige', description: 'Geschaftskommunikation sauber und professionell halten' },
        ],
        'pt-BR': [
          { icon: GraduationCap, title: 'Estudantes', description: 'Limpe notas de pesquisa e rascunhos assistidos por IA para que caracteres ocultos nao corrompam citacoes, quebrem a formatacao ou distorcam a contagem de palavras e caracteres' },
          { icon: PenTool, title: 'Escritores e Blogueiros', description: 'Remova marcas d\'agua de conteudo assistido por IA para publicacao' },
          { icon: FileText, title: 'Usuarios do Word/Google Docs', description: 'Limpe o texto antes de colar em documentos para evitar problemas de formatacao' },
          { icon: Briefcase, title: 'Profissionais', description: 'Garanta que as comunicacoes de negocios estejam limpas e profissionais' },
        ],
        'ms-MY': [
          { icon: GraduationCap, title: 'Pelajar', description: 'Bersihkan nota penyelidikan dan draf yang dibantu AI supaya aksara tersembunyi tidak merosakkan petikan, mengganggu format, atau mengubah kiraan perkataan dan aksara' },
          { icon: PenTool, title: 'Penulis & Blogger', description: 'Buang tanda air dari kandungan yang dibantu AI untuk penerbitan' },
          { icon: FileText, title: 'Pengguna Word/Google Docs', description: 'Bersihkan teks sebelum menampal ke dalam dokumen untuk mengelakkan masalah pemformatan' },
          { icon: Briefcase, title: 'Profesional', description: 'Pastikan komunikasi perniagaan bersih dan profesional' },
        ],
        es: [
          { icon: GraduationCap, title: 'Estudiantes', description: 'Limpia notas de investigacion y borradores asistidos por IA para que los caracteres ocultos no corrompan citas, rompan el formato o distorsionen el recuento de palabras y caracteres' },
          { icon: PenTool, title: 'Escritores y Bloggers', description: 'Elimina marcas de agua de contenido asistido por IA para publicar' },
          { icon: FileText, title: 'Usuarios de Word/Google Docs', description: 'Limpia el texto antes de pegar en documentos para evitar problemas de formato' },
          { icon: Briefcase, title: 'Profesionales', description: 'Asegura que las comunicaciones de negocios esten limpias y profesionales' },
        ],
        fr: [
          { icon: GraduationCap, title: 'Etudiants', description: 'Nettoyez les notes de recherche et brouillons assistes par IA afin que les caracteres caches ne corrompent pas vos citations, ne cassent pas la mise en forme et ne faussent pas vos decomptes de mots et de caracteres' },
          { icon: PenTool, title: 'Ecrivains et Blogueurs', description: 'Supprimez les filigranes du contenu assiste par IA pour la publication' },
          { icon: FileText, title: 'Utilisateurs Word/Google Docs', description: 'Nettoyez le texte avant de le coller dans des documents pour eviter les problemes de formatage' },
          { icon: Briefcase, title: 'Professionnels', description: 'Assurez-vous que les communications professionnelles sont propres et soignees' },
        ],
        ja: [
          { icon: GraduationCap, title: '学生', description: 'AI支援の研究ノートや下書きをクリーニングし、隠れた文字が引用を壊したり、書式を崩したり、単語数・文字数を狂わせたりするのを防ぐ' },
          { icon: PenTool, title: 'ライターとブロガー', description: '公開用にAI支援コンテンツから透かしを削除' },
          { icon: FileText, title: 'Word/Google Docsユーザー', description: 'フォーマットの問題を避けるため、ドキュメントに貼り付ける前にテキストをクリーニング' },
          { icon: Briefcase, title: 'ビジネスプロフェッショナル', description: 'ビジネスコミュニケーションがクリーンでプロフェッショナルであることを確認' },
        ],
        id: [
          { icon: GraduationCap, title: 'Mahasiswa', description: 'Bersihkan catatan penelitian dan draf yang dibantu AI agar karakter tersembunyi tidak merusak kutipan, mengganggu format, atau mengacaukan penghitungan kata dan karakter' },
          { icon: PenTool, title: 'Penulis dan Blogger', description: 'Hapus watermark dari konten yang dibantu AI untuk publikasi' },
          { icon: FileText, title: 'Pengguna Word/Google Docs', description: 'Bersihkan teks sebelum menempelkan ke dokumen untuk menghindari masalah pemformatan' },
          { icon: Briefcase, title: 'Profesional', description: 'Pastikan komunikasi bisnis bersih dan profesional' },
        ],
      },
    },
    socialProof: {
      stats: {
        en: [
          { value: '0', label: 'Data Stored' },
          { value: '100%', label: 'Private & Secure' },
        ],
        de: [
          { value: '0', label: 'Gespeicherte Daten' },
          { value: '100%', label: 'Privat & Sicher' },
        ],
        'pt-BR': [
          { value: '0', label: 'Dados Armazenados' },
          { value: '100%', label: 'Privado e Seguro' },
        ],
        'ms-MY': [
          { value: '0', label: 'Data Disimpan' },
          { value: '100%', label: 'Peribadi & Selamat' },
        ],
        es: [
          { value: '0', label: 'Datos Almacenados' },
          { value: '100%', label: 'Privado y Seguro' },
        ],
        fr: [
          { value: '0', label: 'Donnees Stockees' },
          { value: '100%', label: 'Prive et Securise' },
        ],
        ja: [
          { value: '0', label: '保存されるデータ' },
          { value: '100%', label: 'プライベート＆安全' },
        ],
        id: [
          { value: '0', label: 'Data Disimpan' },
          { value: '100%', label: 'Privat & Aman' },
        ],
      },
    },
    faqTitle: {
      en: 'Free ChatGPT Watermark Remover FAQ',
      de: 'Kostenloser ChatGPT Wasserzeichen-Entferner FAQ',
      'pt-BR': 'Perguntas Frequentes sobre o Removedor de Marca d\'agua ChatGPT Gratis',
      'ms-MY': 'Soalan Lazim Penyingkir Tanda Air ChatGPT Percuma',
      es: 'Preguntas Frecuentes sobre el Eliminador de Marcas de Agua ChatGPT Gratis',
      fr: 'FAQ sur l\'Outil Gratuit de Suppression de Filigrane ChatGPT',
      ja: '無料ChatGPT透かし削除ツールのよくある質問',
      id: 'FAQ Alat Hapus Watermark ChatGPT Gratis',
    },
    relatedContent: {
      title: {
        en: 'Learn More About AI Watermarks',
        de: 'Erfahren Sie mehr ueber KI-Wasserzeichen',
        'pt-BR': 'Saiba Mais Sobre Marcas d\'agua de IA',
        'ms-MY': 'Ketahui Lebih Lanjut Tentang Tanda Air AI',
        es: 'Aprende Mas Sobre las Marcas de Agua de IA',
        fr: 'En Savoir Plus sur les Filigranes IA',
        ja: 'AI透かしについてもっと詳しく',
        id: 'Pelajari Lebih Lanjut Tentang Watermark AI',
      },
    },
  };

  // FAQs for the page
  const faqs = {
    en: [
      {
        question: 'Is this ChatGPT watermark remover really free?',
        answer: 'Yes, our tool is completely free to use. You can clean up to 500 characters per paste without creating an account or paying anything. There are no hidden fees, no trial periods, and no credit card required. The free tier is available forever with unlimited daily uses.',
        icon: Gift,
      },
      {
        question: 'Do I need to create an account to use the free version?',
        answer: 'No, you can start using our free ChatGPT watermark remover immediately without any signup. Just paste your text and click clean. Creating an account is only required if you want to unlock higher character limits with our premium plans.',
        icon: Lock,
      },
      {
        question: 'How many times can I use the free watermark remover?',
        answer: 'There is no limit on how many times you can use the free tier. You can clean text as many times as you want, every day, forever. The only limitation is 500 characters per individual paste operation.',
        icon: Infinity,
      },
      {
        question: 'Is my text private when using the free tool?',
        answer: 'Absolutely. All watermark detection and removal happens entirely in your browser. Your text is never sent to our servers, stored, or logged. This applies to both free and premium users - we prioritize your privacy above all.',
        icon: Shield,
      },
      {
        question: 'What types of characters can the free tool remove?',
        answer: 'Our free tool removes invisible Unicode characters including zero-width spaces (U+200B), zero-width joiners (U+200C, U+200D), word joiners (U+2060), byte order marks (U+FEFF), and soft hyphens (U+00AD).',
        icon: Sparkles,
      },
      {
        question: 'Why is there a 500 character limit on the free tier?',
        answer: 'The 500 character limit allows us to offer the tool completely free while maintaining server costs. For most users cleaning short snippets, this is more than enough. If you need to clean longer texts or documents, our affordable premium plans offer unlimited characters.',
        icon: FileText,
      },
      {
        question: 'Can I use the free tool for academic work?',
        answer: 'Yes, you can use our tool to clean text for academic purposes. However, we encourage ethical use - our tool is designed to remove technical watermarks, not to help with plagiarism. Always properly cite AI-assisted content according to your institution\'s guidelines.',
        icon: GraduationCap,
      },
      {
        question: 'Does the free version work on mobile devices?',
        answer: 'Yes, our free ChatGPT watermark remover works perfectly on mobile devices including smartphones and tablets. The tool is fully responsive and browser-based, so it works on any device with a modern web browser.',
        icon: Zap,
      },
      {
        question: 'How does the free tool detect invisible characters?',
        answer: 'Our tool scans your text against a fixed list of Unicode code points known to appear in AI-generated text and flags every match. The free version uses the exact same detection engine as our premium offering - there is no difference between free and paid tiers.',
        icon: CheckCircle2,
      },
      {
        question: 'Does removing invisible characters affect AI-writing detection?',
        answer: 'No. Detectors like Turnitin and GPTZero analyse your visible writing, patterns in word choice and sentence rhythm, not invisible Unicode characters. Cleaning those characters fixes formatting, code and document issues; it does not change an AI-detection result.',
        icon: BookOpen,
      },
      {
        question: 'Can I clean Word or Google Docs with the free version?',
        answer: 'The free tier supports text-based watermark removal only. For cleaning Word (.docx) or Apple Pages (.pages) documents while preserving formatting, you\'ll need to upgrade to our premium plan. However, you can copy text from documents, clean it with the free tool, and paste it back.',
        icon: FileText,
      },
    ],
    de: [
      {
        question: 'Ist dieser ChatGPT Wasserzeichen-Entferner wirklich kostenlos?',
        answer: 'Ja, unser Tool ist voellig kostenlos. Sie koennen bis zu 500 Zeichen pro Einfuegen bereinigen, ohne ein Konto zu erstellen oder etwas zu bezahlen. Es gibt keine versteckten Gebuehren, keine Testphasen und keine Kreditkarte erforderlich. Die kostenlose Version ist fuer immer mit unbegrenzten taeglichen Nutzungen verfuegbar.',
        icon: Gift,
      },
      {
        question: 'Muss ich ein Konto erstellen, um die kostenlose Version zu nutzen?',
        answer: 'Nein, Sie koennen unseren kostenlosen ChatGPT Wasserzeichen-Entferner sofort ohne Anmeldung nutzen. Fuegen Sie einfach Ihren Text ein und klicken Sie auf Bereinigen. Ein Konto ist nur erforderlich, wenn Sie hoehere Zeichenlimits mit unseren Premium-Plaenen freischalten moechten.',
        icon: Lock,
      },
      {
        question: 'Wie oft kann ich den kostenlosen Wasserzeichen-Entferner nutzen?',
        answer: 'Es gibt keine Begrenzung, wie oft Sie die kostenlose Version nutzen koennen. Sie koennen Text so oft bereinigen, wie Sie moechten, jeden Tag, fuer immer. Die einzige Einschraenkung sind 500 Zeichen pro einzelnem Einfuegevorgang.',
        icon: Infinity,
      },
      {
        question: 'Ist mein Text privat, wenn ich das kostenlose Tool nutze?',
        answer: 'Absolut. Alle Wasserzeichen-Erkennung und -Entfernung erfolgt vollstaendig in Ihrem Browser. Ihr Text wird niemals an unsere Server gesendet, gespeichert oder protokolliert. Dies gilt sowohl fuer kostenlose als auch fuer Premium-Nutzer.',
        icon: Shield,
      },
      {
        question: 'Welche Arten von Wasserzeichen kann das kostenlose Tool entfernen?',
        answer: 'Unser kostenloses Tool entfernt alle Arten von unsichtbaren Unicode-Wasserzeichen, einschliesslich Zero-Width-Spaces (U+200B), Zero-Width-Joiner (U+200C, U+200D), Word-Joiner (U+2060), Byte-Order-Marks (U+FEFF) und Soft-Hyphens (U+00AD).',
        icon: Sparkles,
      },
      {
        question: 'Warum gibt es ein 500-Zeichen-Limit bei der kostenlosen Version?',
        answer: 'Das 500-Zeichen-Limit ermoeglicht es uns, das Tool voellig kostenlos anzubieten und gleichzeitig die Serverkosten zu decken. Fuer die meisten Benutzer, die kurze Textausschnitte bereinigen, ist dies mehr als ausreichend.',
        icon: FileText,
      },
      {
        question: 'Kann ich das kostenlose Tool fuer akademische Arbeiten nutzen?',
        answer: 'Ja, Sie koennen unser Tool nutzen, um Text fuer akademische Zwecke zu bereinigen. Wir ermutigen jedoch zur ethischen Nutzung - unser Tool ist dazu gedacht, technische Wasserzeichen zu entfernen, nicht um bei Plagiaten zu helfen.',
        icon: GraduationCap,
      },
      {
        question: 'Funktioniert die kostenlose Version auf mobilen Geraeten?',
        answer: 'Ja, unser kostenloser ChatGPT Wasserzeichen-Entferner funktioniert perfekt auf mobilen Geraeten einschliesslich Smartphones und Tablets. Das Tool ist vollstaendig responsiv und browserbasiert.',
        icon: Zap,
      },
      {
        question: 'Wie erkennt das kostenlose Tool unsichtbare Zeichen?',
        answer: 'Unser Tool scannt Ihren Text anhand einer festen Liste bekannter Unicode-Codepunkte und markiert jede Uebereinstimmung. Die kostenlose Version verwendet genau dieselbe Erkennungs-Engine wie unser Premium-Angebot - es gibt keinen Unterschied zwischen kostenlos und Premium.',
        icon: CheckCircle2,
      },
      {
        question: 'Beeinflusst das Entfernen unsichtbarer Zeichen die KI-Texterkennung?',
        answer: 'Nein. Detektoren wie Turnitin und GPTZero analysieren Ihren sichtbaren Text, Muster in Wortwahl und Satzrhythmus, nicht unsichtbare Unicode-Zeichen. Das Bereinigen dieser Zeichen behebt Formatierungs-, Code- und Dokumentprobleme; es aendert kein KI-Erkennungsergebnis.',
        icon: BookOpen,
      },
      {
        question: 'Kann ich Word- oder Google Docs mit der kostenlosen Version bereinigen?',
        answer: 'Die kostenlose Version unterstuetzt nur textbasierte Wasserzeichen-Entfernung. Fuer die Bereinigung von Word (.docx) oder Apple Pages (.pages) Dokumenten unter Beibehaltung der Formatierung benoetigen Sie ein Upgrade auf unseren Premium-Plan.',
        icon: FileText,
      },
    ],
    'pt-BR': [
      {
        question: 'Este removedor de marca d\'agua do ChatGPT e realmente gratis?',
        answer: 'Sim, nossa ferramenta e completamente gratuita. Voce pode limpar ate 500 caracteres por colagem sem criar uma conta ou pagar nada. Nao ha taxas ocultas, periodos de teste ou cartao de credito necessario. A versao gratuita esta disponivel para sempre com usos diarios ilimitados.',
        icon: Gift,
      },
      {
        question: 'Preciso criar uma conta para usar a versao gratuita?',
        answer: 'Nao, voce pode comecar a usar nosso removedor de marca d\'agua do ChatGPT imediatamente sem qualquer cadastro. Basta colar seu texto e clicar em limpar. Criar uma conta so e necessario se voce quiser desbloquear limites de caracteres mais altos com nossos planos premium.',
        icon: Lock,
      },
      {
        question: 'Quantas vezes posso usar o removedor de marca d\'agua gratis?',
        answer: 'Nao ha limite de quantas vezes voce pode usar a versao gratuita. Voce pode limpar texto quantas vezes quiser, todos os dias, para sempre. A unica limitacao e 500 caracteres por operacao de colagem individual.',
        icon: Infinity,
      },
      {
        question: 'Meu texto e privado ao usar a ferramenta gratuita?',
        answer: 'Absolutamente. Toda a deteccao e remocao de marca d\'agua acontece inteiramente no seu navegador. Seu texto nunca e enviado para nossos servidores, armazenado ou registrado. Isso se aplica tanto a usuarios gratuitos quanto premium.',
        icon: Shield,
      },
      {
        question: 'Quais tipos de marcas d\'agua a ferramenta gratuita pode remover?',
        answer: 'Nossa ferramenta gratuita remove todos os tipos de marcas d\'agua Unicode invisiveis, incluindo espacos de largura zero (U+200B), juncoes de largura zero (U+200C, U+200D), juncoes de palavras (U+2060), marcas de ordem de byte (U+FEFF) e hifens suaves (U+00AD).',
        icon: Sparkles,
      },
      {
        question: 'Por que ha um limite de 500 caracteres na versao gratuita?',
        answer: 'O limite de 500 caracteres nos permite oferecer a ferramenta completamente gratuita enquanto mantemos os custos do servidor. Para a maioria dos usuarios que limpam trechos curtos, isso e mais que suficiente.',
        icon: FileText,
      },
      {
        question: 'Posso usar a ferramenta gratuita para trabalhos academicos?',
        answer: 'Sim, voce pode usar nossa ferramenta para limpar texto para propositos academicos. No entanto, encorajamos o uso etico - nossa ferramenta e projetada para remover marcas d\'agua tecnicas, nao para ajudar com plagio.',
        icon: GraduationCap,
      },
      {
        question: 'A versao gratuita funciona em dispositivos moveis?',
        answer: 'Sim, nosso removedor de marca d\'agua do ChatGPT gratuito funciona perfeitamente em dispositivos moveis, incluindo smartphones e tablets. A ferramenta e totalmente responsiva e baseada em navegador.',
        icon: Zap,
      },
      {
        question: 'Como a ferramenta gratuita detecta caracteres invisiveis?',
        answer: 'Nossa ferramenta escaneia seu texto com base em uma lista fixa de pontos de codigo Unicode conhecidos e sinaliza cada correspondencia. A versao gratuita usa exatamente o mesmo mecanismo de deteccao que nossa oferta premium - nao ha diferenca entre os planos gratuito e pago.',
        icon: CheckCircle2,
      },
      {
        question: 'Remover caracteres invisiveis afeta a deteccao de escrita por IA?',
        answer: 'Nao. Detectores como Turnitin e GPTZero analisam seu texto visivel, padroes na escolha de palavras e no ritmo das frases, nao caracteres Unicode invisiveis. Limpar esses caracteres resolve problemas de formatacao, codigo e documentos; nao altera o resultado de deteccao de IA.',
        icon: BookOpen,
      },
      {
        question: 'Posso limpar Word ou Google Docs com a versao gratuita?',
        answer: 'A versao gratuita suporta apenas remocao de marca d\'agua baseada em texto. Para limpar documentos Word (.docx) ou Apple Pages (.pages) preservando a formatacao, voce precisara atualizar para nosso plano premium.',
        icon: FileText,
      },
    ],
    'ms-MY': [
      {
        question: 'Adakah penyingkir tanda air ChatGPT ini benar-benar percuma?',
        answer: 'Ya, alat kami adalah percuma sepenuhnya. Anda boleh membersihkan sehingga 500 aksara setiap tampal tanpa membuat akaun atau membayar apa-apa. Tiada yuran tersembunyi, tiada tempoh percubaan, dan tiada kad kredit diperlukan. Peringkat percuma tersedia selama-lamanya dengan penggunaan harian tanpa had.',
        icon: Gift,
      },
      {
        question: 'Adakah saya perlu membuat akaun untuk menggunakan versi percuma?',
        answer: 'Tidak, anda boleh mula menggunakan penyingkir tanda air ChatGPT percuma kami dengan segera tanpa sebarang pendaftaran. Hanya tampal teks anda dan klik bersih. Membuat akaun hanya diperlukan jika anda mahu membuka had aksara yang lebih tinggi dengan pelan premium kami.',
        icon: Lock,
      },
      {
        question: 'Berapa kali saya boleh menggunakan penyingkir tanda air percuma?',
        answer: 'Tiada had berapa kali anda boleh menggunakan peringkat percuma. Anda boleh membersihkan teks seberapa banyak yang anda mahu, setiap hari, selama-lamanya. Satu-satunya had adalah 500 aksara setiap operasi tampal individu.',
        icon: Infinity,
      },
      {
        question: 'Adakah teks saya peribadi apabila menggunakan alat percuma?',
        answer: 'Sudah tentu. Semua pengesanan dan penyingkiran tanda air berlaku sepenuhnya dalam pelayar anda. Teks anda tidak pernah dihantar ke pelayan kami, disimpan, atau dilog. Ini terpakai kepada pengguna percuma dan premium.',
        icon: Shield,
      },
      {
        question: 'Apakah jenis tanda air yang boleh dibuang oleh alat percuma?',
        answer: 'Alat percuma kami membuang semua jenis tanda air Unicode tidak kelihatan termasuk ruang lebar sifar (U+200B), penyambung lebar sifar (U+200C, U+200D), penyambung perkataan (U+2060), tanda susunan bait (U+FEFF), dan tanda sempang lembut (U+00AD).',
        icon: Sparkles,
      },
      {
        question: 'Mengapa ada had 500 aksara pada peringkat percuma?',
        answer: 'Had 500 aksara membolehkan kami menawarkan alat ini secara percuma sepenuhnya sambil mengekalkan kos pelayan. Bagi kebanyakan pengguna yang membersihkan potongan pendek, ini lebih daripada mencukupi.',
        icon: FileText,
      },
      {
        question: 'Bolehkah saya menggunakan alat percuma untuk kerja akademik?',
        answer: 'Ya, anda boleh menggunakan alat kami untuk membersihkan teks untuk tujuan akademik. Walau bagaimanapun, kami menggalakkan penggunaan beretika - alat kami direka untuk membuang tanda air teknikal, bukan untuk membantu plagiarisme.',
        icon: GraduationCap,
      },
      {
        question: 'Adakah versi percuma berfungsi pada peranti mudah alih?',
        answer: 'Ya, penyingkir tanda air ChatGPT percuma kami berfungsi dengan sempurna pada peranti mudah alih termasuk telefon pintar dan tablet. Alat ini sepenuhnya responsif dan berasaskan pelayar.',
        icon: Zap,
      },
      {
        question: 'Bagaimana alat percuma mengesan aksara tidak kelihatan?',
        answer: 'Alat kami mengimbas teks anda berdasarkan senarai tetap titik kod Unicode yang diketahui dan menandakan setiap padanan. Versi percuma menggunakan enjin pengesanan yang sama persis seperti tawaran premium kami - tiada perbezaan antara peringkat percuma dan berbayar.',
        icon: CheckCircle2,
      },
      {
        question: 'Adakah membuang aksara tidak kelihatan menjejaskan pengesanan penulisan AI?',
        answer: 'Tidak. Pengesan seperti Turnitin dan GPTZero menganalisis teks anda yang kelihatan, corak dalam pemilihan perkataan dan rentak ayat, bukan aksara Unicode tidak kelihatan. Membersihkan aksara tersebut menyelesaikan masalah format, kod dan dokumen; ia tidak mengubah keputusan pengesanan AI.',
        icon: BookOpen,
      },
      {
        question: 'Bolehkah saya membersihkan Word atau Google Docs dengan versi percuma?',
        answer: 'Peringkat percuma menyokong penyingkiran tanda air berasaskan teks sahaja. Untuk membersihkan dokumen Word (.docx) atau Apple Pages (.pages) sambil mengekalkan pemformatan, anda perlu menaik taraf ke pelan premium kami.',
        icon: FileText,
      },
    ],
    es: [
      {
        question: 'Es este eliminador de marcas de agua de ChatGPT realmente gratis?',
        answer: 'Si, nuestra herramienta es completamente gratuita. Puedes limpiar hasta 500 caracteres por pegado sin crear una cuenta ni pagar nada. No hay tarifas ocultas, periodos de prueba ni tarjeta de credito requerida. La version gratuita esta disponible para siempre con usos diarios ilimitados.',
        icon: Gift,
      },
      {
        question: 'Necesito crear una cuenta para usar la version gratuita?',
        answer: 'No, puedes comenzar a usar nuestro eliminador de marcas de agua de ChatGPT gratuito inmediatamente sin ningun registro. Solo pega tu texto y haz clic en limpiar. Crear una cuenta solo es necesario si quieres desbloquear limites de caracteres mas altos con nuestros planes premium.',
        icon: Lock,
      },
      {
        question: 'Cuantas veces puedo usar el eliminador de marcas de agua gratis?',
        answer: 'No hay limite de cuantas veces puedes usar la version gratuita. Puedes limpiar texto tantas veces como quieras, todos los dias, para siempre. La unica limitacion es 500 caracteres por operacion de pegado individual.',
        icon: Infinity,
      },
      {
        question: 'Es mi texto privado cuando uso la herramienta gratuita?',
        answer: 'Absolutamente. Toda la deteccion y eliminacion de marcas de agua ocurre completamente en tu navegador. Tu texto nunca se envia a nuestros servidores, se almacena ni se registra. Esto aplica tanto para usuarios gratuitos como premium.',
        icon: Shield,
      },
      {
        question: 'Que tipos de marcas de agua puede eliminar la herramienta gratuita?',
        answer: 'Nuestra herramienta gratuita elimina todos los tipos de marcas de agua Unicode invisibles, incluyendo espacios de ancho cero (U+200B), uniones de ancho cero (U+200C, U+200D), uniones de palabras (U+2060), marcas de orden de bytes (U+FEFF) e guiones suaves (U+00AD).',
        icon: Sparkles,
      },
      {
        question: 'Por que hay un limite de 500 caracteres en la version gratuita?',
        answer: 'El limite de 500 caracteres nos permite ofrecer la herramienta completamente gratis mientras mantenemos los costos del servidor. Para la mayoria de los usuarios que limpian fragmentos cortos, esto es mas que suficiente.',
        icon: FileText,
      },
      {
        question: 'Puedo usar la herramienta gratuita para trabajos academicos?',
        answer: 'Si, puedes usar nuestra herramienta para limpiar texto para propositos academicos. Sin embargo, fomentamos el uso etico - nuestra herramienta esta disenada para eliminar marcas de agua tecnicas, no para ayudar con el plagio.',
        icon: GraduationCap,
      },
      {
        question: 'Funciona la version gratuita en dispositivos moviles?',
        answer: 'Si, nuestro eliminador de marcas de agua de ChatGPT gratuito funciona perfectamente en dispositivos moviles incluyendo telefonos inteligentes y tabletas. La herramienta es totalmente responsiva y basada en navegador.',
        icon: Zap,
      },
      {
        question: 'Como detecta la herramienta gratuita los caracteres invisibles?',
        answer: 'Nuestra herramienta escanea tu texto segun una lista fija de puntos de codigo Unicode conocidos y marca cada coincidencia. La version gratuita usa exactamente el mismo motor de deteccion que nuestra oferta premium; no hay diferencia entre los planes gratuito y de pago.',
        icon: CheckCircle2,
      },
      {
        question: 'Eliminar caracteres invisibles afecta la deteccion de escritura por IA?',
        answer: 'No. Detectores como Turnitin y GPTZero analizan tu texto visible, patrones en la eleccion de palabras y el ritmo de las frases, no caracteres Unicode invisibles. Limpiar esos caracteres resuelve problemas de formato, codigo y documentos; no cambia el resultado de deteccion de IA.',
        icon: BookOpen,
      },
      {
        question: 'Puedo limpiar Word o Google Docs con la version gratuita?',
        answer: 'La version gratuita solo admite la eliminacion de marcas de agua basada en texto. Para limpiar documentos Word (.docx) o Apple Pages (.pages) preservando el formato, necesitaras actualizar a nuestro plan premium.',
        icon: FileText,
      },
    ],
    fr: [
      {
        question: 'Cet outil de suppression de filigrane IA ChatGPT est-il vraiment gratuit ?',
        answer: 'Oui, notre outil est entierement gratuit a utiliser. Vous pouvez nettoyer jusqu\'a 500 caracteres par collage sans creer de compte ni payer quoi que ce soit. Il n\'y a pas de frais caches, pas de periodes d\'essai et pas de carte de credit requise. Le niveau gratuit est disponible pour toujours avec des utilisations quotidiennes illimitees.',
        icon: Gift,
      },
      {
        question: 'Dois-je creer un compte pour utiliser la version gratuite ?',
        answer: 'Non, vous pouvez commencer a utiliser notre outil de suppression de filigrane IA ChatGPT gratuit immediatement sans aucune inscription. Collez simplement votre texte et cliquez sur nettoyer. Creer un compte n\'est necessaire que si vous souhaitez debloquer des limites de caracteres plus elevees avec nos forfaits premium.',
        icon: Lock,
      },
      {
        question: 'Combien de fois puis-je utiliser l\'outil de suppression de filigrane IA gratuit ?',
        answer: 'Il n\'y a pas de limite au nombre de fois que vous pouvez utiliser le niveau gratuit. Vous pouvez nettoyer du texte autant de fois que vous le souhaitez, chaque jour, pour toujours. La seule limitation est de 500 caracteres par operation de collage individuelle.',
        icon: Infinity,
      },
      {
        question: 'Mon texte est-il prive lorsque j\'utilise l\'outil gratuit ?',
        answer: 'Absolument. Toute la detection et la suppression de filigrane se fait entierement dans votre navigateur. Votre texte n\'est jamais envoye a nos serveurs, stocke ou enregistre. Cela s\'applique aux utilisateurs gratuits comme premium - nous accordons la priorite a votre vie privee.',
        icon: Shield,
      },
      {
        question: 'Quels types de caracteres l\'outil gratuit peut-il supprimer ?',
        answer: 'Notre outil gratuit supprime les caracteres Unicode invisibles, y compris les espaces de largeur nulle (U+200B), les jointeurs de largeur nulle (U+200C, U+200D), les jointeurs de mots (U+2060), les marques d\'ordre des octets (U+FEFF) et les traits d\'union conditionnels (U+00AD).',
        icon: Sparkles,
      },
      {
        question: 'Pourquoi y a-t-il une limite de 500 caracteres sur le niveau gratuit ?',
        answer: 'La limite de 500 caracteres nous permet d\'offrir l\'outil completement gratuitement tout en maintenant les couts des serveurs. Pour la plupart des utilisateurs qui nettoient de courts extraits, c\'est plus que suffisant. Si vous devez nettoyer des textes ou des documents plus longs, nos forfaits premium abordables offrent des caracteres illimites.',
        icon: FileText,
      },
      {
        question: 'Puis-je utiliser l\'outil gratuit pour un travail academique ?',
        answer: 'Oui, vous pouvez utiliser notre outil pour nettoyer du texte a des fins academiques. Cependant, nous encourageons une utilisation ethique - notre outil est concu pour supprimer les filigranes techniques, pas pour aider au plagiat. Citez toujours correctement le contenu assiste par l\'IA selon les directives de votre institution.',
        icon: GraduationCap,
      },
      {
        question: 'La version gratuite fonctionne-t-elle sur les appareils mobiles ?',
        answer: 'Oui, notre outil de suppression de filigrane IA ChatGPT gratuit fonctionne parfaitement sur les appareils mobiles, y compris les smartphones et les tablettes. L\'outil est entierement responsive et base sur navigateur, il fonctionne donc sur n\'importe quel appareil avec un navigateur web moderne.',
        icon: Zap,
      },
      {
        question: 'Comment l\'outil gratuit detecte-t-il les caracteres invisibles ?',
        answer: 'Notre outil analyse votre texte selon une liste fixe de points de code Unicode connus et signale chaque correspondance. La version gratuite utilise exactement le meme moteur de detection que notre offre premium - il n\'y a aucune difference entre les niveaux gratuit et payant.',
        icon: CheckCircle2,
      },
      {
        question: 'La suppression des caracteres invisibles affecte-t-elle la detection d\'ecriture IA ?',
        answer: 'Non. Les detecteurs comme Turnitin et GPTZero analysent votre texte visible, les schemas du choix des mots et du rythme des phrases, et non les caracteres Unicode invisibles. Nettoyer ces caracteres corrige les problemes de mise en forme, de code et de documents ; cela ne change pas un resultat de detection IA.',
        icon: BookOpen,
      },
      {
        question: 'Puis-je nettoyer Word ou Google Docs avec la version gratuite ?',
        answer: 'Le niveau gratuit prend en charge uniquement la suppression de filigrane basee sur le texte. Pour nettoyer des documents Word (.docx) ou Apple Pages (.pages) tout en preservant la mise en forme, vous devrez passer a notre forfait premium. Cependant, vous pouvez copier le texte des documents, le nettoyer avec l\'outil gratuit et le coller.',
        icon: FileText,
      },
    ],
    ja: [
      {
        question: 'このChatGPT AI透かし削除ツールは本当に無料ですか？',
        answer: 'はい、私たちのツールは完全に無料で使用できます。アカウントを作成したり、何かを支払ったりすることなく、貼り付けごとに最大500文字をクリーニングできます。隠れた料金、試用期間、クレジットカードは必要ありません。無料枠は毎日無制限で永久に利用可能です。',
        icon: Gift,
      },
      {
        question: '無料版を使用するためにアカウントを作成する必要がありますか？',
        answer: 'いいえ、サインアップなしですぐに無料のChatGPT AI透かし削除ツールを使い始めることができます。テキストを貼り付けてクリーンをクリックするだけです。アカウントの作成は、プレミアムプランでより高い文字制限を解除したい場合にのみ必要です。',
        icon: Lock,
      },
      {
        question: '無料のAI透かし削除ツールを何回使用できますか？',
        answer: '無料枠の使用回数に制限はありません。毎日、永久に、好きなだけテキストをクリーニングできます。唯一の制限は、個々の貼り付け操作ごとに500文字です。',
        icon: Infinity,
      },
      {
        question: '無料ツールを使用する際、私のテキストはプライベートですか？',
        answer: '絶対にそうです。すべての透かし検出と削除はブラウザ内で完全に行われます。テキストがサーバーに送信されたり、保存されたり、ログに記録されたりすることはありません。これは無料ユーザーとプレミアムユーザーの両方に適用されます - 私たちはあなたのプライバシーを最優先にしています。',
        icon: Shield,
      },
      {
        question: '無料ツールはどのような種類の文字を削除できますか？',
        answer: '私たちの無料ツールは、ゼロ幅スペース（U+200B）、ゼロ幅結合子（U+200C、U+200D）、ワードジョイナー（U+2060）、バイトオーダーマーク（U+FEFF）、ソフトハイフン（U+00AD）を含む、不可視のUnicode文字を除去します。',
        icon: Sparkles,
      },
      {
        question: 'なぜ無料枠に500文字の制限があるのですか？',
        answer: '500文字の制限により、サーバーコストを維持しながらツールを完全に無料で提供することができます。短いスニペットをクリーニングするほとんどのユーザーにとって、これで十分です。より長いテキストやドキュメントをクリーニングする必要がある場合は、手頃なプレミアムプランで無制限の文字数を提供しています。',
        icon: FileText,
      },
      {
        question: '学術的な作業に無料ツールを使用できますか？',
        answer: 'はい、学術目的でテキストをクリーニングするために私たちのツールを使用できます。ただし、倫理的な使用をお勧めします - 私たちのツールは技術的な透かしを削除するために設計されており、盗作を助けるためではありません。機関のガイドラインに従って、AIアシストされたコンテンツを常に適切に引用してください。',
        icon: GraduationCap,
      },
      {
        question: '無料版はモバイルデバイスで動作しますか？',
        answer: 'はい、私たちの無料ChatGPT AI透かし削除ツールは、スマートフォンやタブレットを含むモバイルデバイスで完璧に動作します。ツールは完全にレスポンシブでブラウザベースなので、最新のWebブラウザを搭載したどのデバイスでも動作します。',
        icon: Zap,
      },
      {
        question: '無料ツールはどのように不可視文字を検出しますか？',
        answer: '当ツールは既知のUnicodeコードポイントの固定リストに基づいてテキストをスキャンし、一致したものすべてにフラグを立てます。無料版はプレミアム版と全く同じ検出エンジンを使用しています。無料と有料のティア間で違いはありません。',
        icon: CheckCircle2,
      },
      {
        question: '不可視文字の除去はAI文章検出に影響しますか？',
        answer: 'いいえ。TurnitinやGPTZeroなどの検出ツールは、不可視のUnicode文字ではなく、表示される文章、単語選択や文のリズムのパターンを分析します。これらの文字を除去すると書式・コード・ドキュメントの問題は解消されますが、AI検出結果は変わりません。',
        icon: BookOpen,
      },
      {
        question: '無料版でWordまたはGoogle Docsをクリーニングできますか？',
        answer: '無料枠はテキストベースの透かし削除のみをサポートしています。フォーマットを保持しながらWord（.docx）またはApple Pages（.pages）ドキュメントをクリーニングするには、プレミアムプランにアップグレードする必要があります。ただし、ドキュメントからテキストをコピーし、無料ツールでクリーニングして貼り付けることができます。',
        icon: FileText,
      },
    ],
    id: [
      {
        question: 'Apakah alat hapus watermark AI ChatGPT ini benar-benar gratis?',
        answer: 'Ya, alat kami sepenuhnya gratis untuk digunakan. Anda dapat membersihkan hingga 500 karakter per tempel tanpa membuat akun atau membayar apa pun. Tidak ada biaya tersembunyi, tidak ada periode percobaan, dan tidak diperlukan kartu kredit. Tingkat gratis tersedia selamanya dengan penggunaan harian tanpa batas.',
        icon: Gift,
      },
      {
        question: 'Apakah saya perlu membuat akun untuk menggunakan versi gratis?',
        answer: 'Tidak, Anda dapat langsung mulai menggunakan alat hapus watermark AI ChatGPT gratis kami tanpa pendaftaran apa pun. Cukup tempelkan teks Anda dan klik bersihkan. Membuat akun hanya diperlukan jika Anda ingin membuka batas karakter yang lebih tinggi dengan paket premium kami.',
        icon: Lock,
      },
      {
        question: 'Berapa kali saya dapat menggunakan alat hapus watermark AI gratis?',
        answer: 'Tidak ada batasan berapa kali Anda dapat menggunakan tingkat gratis. Anda dapat membersihkan teks sebanyak yang Anda inginkan, setiap hari, selamanya. Satu-satunya batasan adalah 500 karakter per operasi tempel individu.',
        icon: Infinity,
      },
      {
        question: 'Apakah teks saya pribadi saat menggunakan alat gratis?',
        answer: 'Tentu saja. Semua deteksi dan penghapusan watermark terjadi sepenuhnya di browser Anda. Teks Anda tidak pernah dikirim ke server kami, disimpan, atau dicatat. Ini berlaku untuk pengguna gratis dan premium - kami mengutamakan privasi Anda.',
        icon: Shield,
      },
      {
        question: 'Jenis karakter apa yang dapat dihapus oleh alat gratis?',
        answer: 'Alat gratis kami menghapus karakter Unicode tak terlihat termasuk spasi lebar-nol (U+200B), penyambung lebar-nol (U+200C, U+200D), penyambung kata (U+2060), tanda urutan byte (U+FEFF), dan tanda hubung lunak (U+00AD).',
        icon: Sparkles,
      },
      {
        question: 'Mengapa ada batas 500 karakter pada tingkat gratis?',
        answer: 'Batas 500 karakter memungkinkan kami menawarkan alat ini sepenuhnya gratis sambil mempertahankan biaya server. Untuk sebagian besar pengguna yang membersihkan cuplikan pendek, ini lebih dari cukup. Jika Anda perlu membersihkan teks atau dokumen yang lebih panjang, paket premium kami yang terjangkau menawarkan karakter tanpa batas.',
        icon: FileText,
      },
      {
        question: 'Bisakah saya menggunakan alat gratis untuk pekerjaan akademis?',
        answer: 'Ya, Anda dapat menggunakan alat kami untuk membersihkan teks untuk tujuan akademis. Namun, kami mendorong penggunaan etis - alat kami dirancang untuk menghapus watermark teknis, bukan untuk membantu plagiarisme. Selalu kutip konten yang dibantu AI dengan benar sesuai dengan pedoman institusi Anda.',
        icon: GraduationCap,
      },
      {
        question: 'Apakah versi gratis berfungsi di perangkat seluler?',
        answer: 'Ya, alat hapus watermark AI ChatGPT gratis kami berfungsi dengan sempurna di perangkat seluler termasuk smartphone dan tablet. Alat ini sepenuhnya responsif dan berbasis browser, sehingga berfungsi di perangkat apa pun dengan browser web modern.',
        icon: Zap,
      },
      {
        question: 'Bagaimana alat gratis mendeteksi karakter tak terlihat?',
        answer: 'Alat kami memindai teks Anda berdasarkan daftar tetap titik kode Unicode yang diketahui dan menandai setiap kecocokan. Versi gratis menggunakan mesin deteksi yang persis sama dengan penawaran premium kami - tidak ada perbedaan antara tingkat gratis dan berbayar.',
        icon: CheckCircle2,
      },
      {
        question: 'Apakah menghapus karakter tak terlihat memengaruhi deteksi tulisan AI?',
        answer: 'Tidak. Detektor seperti Turnitin dan GPTZero menganalisis teks Anda yang terlihat, pola dalam pemilihan kata dan ritme kalimat, bukan karakter Unicode tak terlihat. Membersihkan karakter tersebut memperbaiki masalah format, kode, dan dokumen; tidak mengubah hasil deteksi AI.',
        icon: BookOpen,
      },
      {
        question: 'Bisakah saya membersihkan Word atau Google Docs dengan versi gratis?',
        answer: 'Tingkat gratis mendukung penghapusan watermark berbasis teks saja. Untuk membersihkan dokumen Word (.docx) atau Apple Pages (.pages) sambil mempertahankan pemformatan, Anda perlu meningkatkan ke paket premium kami. Namun, Anda dapat menyalin teks dari dokumen, membersihkannya dengan alat gratis, dan menempelkannya kembali.',
        icon: FileText,
      },
    ],
  };

  const currentFAQs = faqs[locale as LocaleKey] || faqs.en;
  const currentLocale = locale as LocaleKey;

  // Inject FAQ Schema JSON-LD for SEO
  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const faqSchema = generateFAQSchema(currentFAQs);
      const cleanup = injectFAQSchema(faqSchema);
      return cleanup;
    } catch (error) {
      console.error('[FreeChatGPT FAQ] Failed to generate or inject FAQ schema:', error);
    }
  }, [locale, currentFAQs]);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 px-4">
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute left-1/2 top-0 -translate-x-1/2 blur-3xl opacity-30 w-[40rem] h-[40rem]">
            <div className="h-full w-full rounded-full bg-gradient-to-br from-green-400 via-emerald-400 to-teal-400"></div>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-4 py-2 rounded-full text-sm font-semibold mb-6 border border-green-200 dark:border-green-800">
              <Gift size={16} />
              {t.hero.badge[currentLocale]}
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-neutral-900 dark:text-white mb-6 leading-tight">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600 dark:from-green-400 dark:to-emerald-400">
                {t.hero.title[currentLocale]}
              </span>
            </h1>

            <p className="text-xl text-neutral-600 dark:text-neutral-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              {t.hero.subtitle[currentLocale]}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="#remover"
                className="group inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-xl font-semibold professional-transition shadow-lg hover:shadow-xl active:scale-[0.98]"
              >
                {t.hero.cta[currentLocale]}
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>

            <div className="flex justify-center pt-6">
              <SocialProof className="max-w-md" />
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-6 max-w-xl mx-auto">
            {t.socialProof.stats[currentLocale].map((stat, index) => (
              <div key={index} className="bg-white dark:bg-charcoal-800 p-6 rounded-xl shadow-lg text-center border border-neutral-200 dark:border-charcoal-700">
                <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">{stat.value}</div>
                <div className="text-sm text-neutral-600 dark:text-neutral-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tool Section */}
      <section id="remover" className="mt-8">
        <div className="max-w-[1400px] mx-auto px-4 mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white text-center mb-4">
            {t.toolSection.title[currentLocale]}
          </h2>
          <p className="text-lg text-neutral-600 dark:text-neutral-300 text-center max-w-2xl mx-auto">
            {t.toolSection.description[currentLocale]}
          </p>
        </div>
        <div className="max-w-[1400px] mx-auto px-4">
          <WatermarkRemoverClient user={user} />
        </div>
      </section>

      {/* Free Tier Benefits Section */}
      <section className="mt-16 max-w-[1400px] mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white text-center mb-12">
          {t.benefits.title[currentLocale]}
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.benefits.items[currentLocale].map((item, index) => {
            const Icon = item.icon;
            return (
              <div key={index} className="bg-white dark:bg-charcoal-800 p-6 rounded-xl shadow-lg border border-neutral-200 dark:border-charcoal-700">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center mb-4">
                  <Icon size={24} className="text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-lg font-bold text-neutral-900 dark:text-white mb-2">{item.title}</h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">{item.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Explanation Section */}
      <section className="mt-16 max-w-[1400px] mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white text-center mb-8">
          {t.explanation.title[currentLocale]}
        </h2>
        <div className="bg-white dark:bg-charcoal-800 rounded-2xl p-8 shadow-lg border border-neutral-200 dark:border-charcoal-700 space-y-6">
          {t.explanation.paragraphs[currentLocale].map((paragraph, index) => (
            <p key={index} className="text-neutral-700 dark:text-neutral-300 leading-relaxed">
              {paragraph}
            </p>
          ))}
        </div>
      </section>

      {/* Comparison Section */}
      <section className="mt-16 max-w-[1400px] mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white text-center mb-8">
          {t.comparison.title[currentLocale]}
        </h2>
        <div className="overflow-x-auto rounded-2xl border border-neutral-200 dark:border-charcoal-700">
          <table className="w-full border-collapse bg-white dark:bg-charcoal-800">
            <thead>
              <tr className="bg-gradient-to-r from-green-600 to-emerald-600 text-white">
                <th className="p-4 text-left font-bold">{t.comparison.featureLabel[currentLocale]}</th>
                <th className="p-4 text-center font-bold">{t.comparison.free[currentLocale]}</th>
                <th className="p-4 text-center font-bold">{t.comparison.premium[currentLocale]}</th>
              </tr>
            </thead>
            <tbody>
              {t.comparison.features[currentLocale].map((row, index) => (
                <tr key={index} className="border-b border-neutral-200 dark:border-charcoal-700">
                  <td className="p-4 font-medium text-neutral-900 dark:text-white">{row.feature}</td>
                  <td className="p-4 text-center text-neutral-700 dark:text-neutral-300">{row.free}</td>
                  <td className="p-4 text-center text-neutral-700 dark:text-neutral-300">{row.premium}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="mt-16 max-w-[1400px] mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white text-center mb-12">
          {t.useCases.title[currentLocale]}
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.useCases.items[currentLocale].map((item, index) => {
            const Icon = item.icon;
            return (
              <div key={index} className="bg-white dark:bg-charcoal-800 p-6 rounded-xl shadow-lg border border-neutral-200 dark:border-charcoal-700 hover:shadow-xl transition-shadow">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                  <Icon size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-bold text-neutral-900 dark:text-white mb-2">{item.title}</h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">{item.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Related Content for pSEO */}
      <div className="mt-16 max-w-[1400px] mx-auto px-4">
        <RelatedContentForPSEO
          pageId="free-chatgpt-watermark"
          pageTitle={t.relatedContent.title[currentLocale]}
          pageDescription={
            locale === 'de'
              ? 'Entfernen Sie unsichtbare Zeichen aus ChatGPT-Text'
              : locale === 'pt-BR'
              ? 'Remova caracteres invisiveis do texto ChatGPT'
              : locale === 'ms-MY'
              ? 'Buang aksara tidak kelihatan dari teks ChatGPT'
              : locale === 'es'
              ? 'Elimina caracteres invisibles del texto de ChatGPT'
              : locale === 'fr'
              ? 'Supprimez les caracteres invisibles du texte ChatGPT'
              : locale === 'ja'
              ? 'ChatGPTテキストから不可視文字を削除'
              : locale === 'id'
              ? 'Hapus karakter tak terlihat dari teks ChatGPT'
              : 'Remove invisible characters from ChatGPT text'
          }
          locale={locale}
        />
      </div>

      {/* FAQ Section */}
      <section className="mt-16 max-w-[1400px] mx-auto px-4">
        <div className="bg-gradient-to-br from-green-50 via-neutral-100 to-emerald-50 dark:from-charcoal-900 dark:via-charcoal-850 dark:to-charcoal-900 rounded-2xl shadow-xl p-8 border border-neutral-200 dark:border-charcoal-700/50">
          <h2 className="text-3xl font-bold mb-8 text-neutral-900 dark:text-white text-center flex items-center justify-center gap-3">
            <HelpCircle size={32} className="text-green-600 dark:text-green-400" />
            {t.faqTitle[currentLocale]}
          </h2>

          <div className="space-y-3">
            {currentFAQs.map((faq, index) => {
              const Icon = faq.icon;
              const isOpen = openFAQIndex === index;

              return (
                <div
                  key={index}
                  className="border border-neutral-200 dark:border-charcoal-700/50 rounded-xl overflow-hidden bg-white/60 dark:bg-white/5 backdrop-blur-sm transition-all duration-300 hover:bg-white/80 dark:hover:bg-white/10"
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full flex items-center justify-between p-6 text-left professional-transition"
                  >
                    <div className="flex items-start gap-4 flex-1">
                      <div className="p-2 bg-green-200 dark:bg-white/15 rounded-lg flex-shrink-0">
                        <Icon size={24} className="text-green-700 dark:text-white" />
                      </div>
                      <span className="font-semibold text-neutral-900 dark:text-white text-lg">
                        {faq.question}
                      </span>
                    </div>
                    <div className={`p-1.5 rounded-lg transition-all duration-300 ml-4 ${isOpen ? 'bg-green-200/50 dark:bg-white/10 rotate-180' : 'bg-neutral-100 dark:bg-white/5'}`}>
                      <ChevronDown className={`w-4 h-4 transition-colors ${isOpen ? 'text-green-700 dark:text-white' : 'text-neutral-500 dark:text-neutral-400'}`} />
                    </div>
                  </button>

                  <div className={`px-6 overflow-hidden transition-all duration-500 ease-in-out ${isOpen ? 'max-h-[500px] pb-6' : 'max-h-0'}`}>
                    <div className="pl-16 border-l-2 border-green-200 dark:border-white/10 ml-2">
                      <p className="text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed pl-4">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Internal linking banner - Gemini Image Watermark Remover */}
      <GeminiImageBanner />

      {/* Related Tools */}
      <Suspense fallback={null}>
        <RelatedTools currentPage="free-chatgpt" />
      </Suspense>

      {/* Feedback CTA */}
      <Suspense fallback={null}>
        <FeedbackCTA />
      </Suspense>
    </div>
  );
}
